import type { TeamsShift, TeamsTimecard } from '../api/types';

/** How far a clock-in may drift from a shift's start and still count as that shift. */
const MATCH_TOLERANCE_MS = 3 * 60 * 60 * 1000;

/**
 * Finds the scheduled shift a timecard was worked against: same employee, same
 * location, and the shift whose time overlaps the punch most. With no overlap,
 * falls back to the shift starting nearest the clock-in, within tolerance.
 */
export function matchShift(card: TeamsTimecard, shifts: TeamsShift[]): TeamsShift | undefined {
  if (!card.clockIn) return undefined;
  const inMs  = new Date(card.clockIn.dateTime).getTime();
  const outMs = card.clockOut ? new Date(card.clockOut.dateTime).getTime() : inMs;

  let best: TeamsShift | undefined;
  let bestOverlap = 0;
  let bestGap = Infinity;

  for (const s of shifts) {
    if (s.entraObjectId !== card.entraObjectId || s.locationId !== card.locationId) continue;
    if (!s.sharedStart || !s.sharedEnd) continue;
    const startMs = new Date(s.sharedStart).getTime();
    const endMs   = new Date(s.sharedEnd).getTime();

    const overlap = Math.min(outMs, endMs) - Math.max(inMs, startMs);
    if (overlap > bestOverlap) {
      best = s;
      bestOverlap = overlap;
      continue;
    }
    if (bestOverlap > 0) continue;

    const gap = Math.abs(startMs - inMs);
    if (gap <= MATCH_TOLERANCE_MS && gap < bestGap) {
      best = s;
      bestGap = gap;
    }
  }

  return best;
}
